import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Calendar as CalendarIcon, Plus, Trash2, Users, TrendingUp, Mail, CheckCircle, Clock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CommitteeAvailability({ user, logout }) {
  const navigate = useNavigate();
  const [myAvailability, setMyAvailability] = useState([]);
  const [timeUnavailability, setTimeUnavailability] = useState([]);
  const [committeeAvailability, setCommitteeAvailability] = useState([]);
  const [bestDates, setBestDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showBestDatesDialog, setShowBestDatesDialog] = useState(false);
  
  // Calendar connection states
  const [outlookStatus, setOutlookStatus] = useState(null);
  const [googleStatus, setGoogleStatus] = useState(null);
  const [checkingCalendars, setCheckingCalendars] = useState(false);
  
  // Form fields for date-based unavailability (legacy)
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reason, setReason] = useState("");
  
  // Form fields for time-based unavailability
  const [unavailDate, setUnavailDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [unavailReason, setUnavailReason] = useState("");
  const [isAllDay, setIsAllDay] = useState(false);
  
  // Best dates search
  const [searchStartDate, setSearchStartDate] = useState("");
  const [searchEndDate, setSearchEndDate] = useState("");

  const isCommitteeMember = ["convenor", "captain", "committee_member", "secretary", "treasurer", "jsm_representative"].includes(user.role);
  const canFindBestDates = ["convenor", "captain", "secretary"].includes(user.role);

  useEffect(() => {
    if (isCommitteeMember) {
      fetchMyAvailability();
      fetchTimeUnavailability();
      fetchCommitteeAvailability();
      checkCalendarConnections();
    }
  }, []);

  const fetchMyAvailability = async () => {
    try {
      const response = await axios.get(`${API}/my-availability`, {
        withCredentials: true
      });
      setMyAvailability(response.data);
    } catch (error) {
      console.error("Error fetching availability:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTimeUnavailability = async () => {
    try {
      const response = await axios.get(`${API}/calendars/unavailability`, {
        withCredentials: true
      });
      setTimeUnavailability(response.data.unavailabilities || []);
    } catch (error) {
      console.error("Error fetching time unavailability:", error);
    }
  };

  const addTimeUnavailability = async () => {
    if (!unavailDate) {
      toast.error("Please select a date");
      return;
    }
    
    if (!isAllDay && (!startTime || !endTime)) {
      toast.error("Please select start and end times");
      return;
    }
    
    try {
      let startDatetime, endDatetime;
      
      if (isAllDay) {
        startDatetime = `${unavailDate}T00:00:00`;
        endDatetime = `${unavailDate}T23:59:59`;
      } else {
        startDatetime = `${unavailDate}T${startTime}:00`;
        endDatetime = `${unavailDate}T${endTime}:00`;
      }
      
      await axios.post(`${API}/calendars/unavailability`, {
        start_datetime: startDatetime,
        end_datetime: endDatetime,
        reason: unavailReason || null,
        is_all_day: isAllDay
      }, { withCredentials: true });
      
      toast.success("Unavailability added");
      setUnavailDate("");
      setStartTime("");
      setEndTime("");
      setUnavailReason("");
      setIsAllDay(false);
      fetchTimeUnavailability();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add unavailability");
    }
  };

  const deleteTimeUnavailability = async (id) => {
    if (!window.confirm("Remove this unavailability?")) return;
    
    try {
      await axios.delete(`${API}/calendars/unavailability/${id}`, {
        withCredentials: true
      });
      toast.success("Removed");
      fetchTimeUnavailability();
    } catch (error) {
      toast.error("Failed to remove");
    }
  };

  const fetchCommitteeAvailability = async () => {
    try {
      const response = await axios.get(`${API}/committee/availability`, {
        withCredentials: true
      });
      setCommitteeAvailability(response.data.availability || []);
    } catch (error) {
      console.error("Error fetching committee availability:", error);
    }
  };

  const checkCalendarConnections = async () => {
    setCheckingCalendars(true);
    try {
      // Check Outlook
      const outlookResponse = await axios.get(`${API}/outlook-integration/status`, {
        withCredentials: true
      });
      setOutlookStatus(outlookResponse.data);
    } catch (error) {
      console.error("Error checking Outlook:", error);
      setOutlookStatus({ connected: false });
    }
    
    try {
      // Check Google
      const googleResponse = await axios.get(`${API}/google-integration/status`, {
        withCredentials: true
      });
      setGoogleStatus(googleResponse.data);
    } catch (error) {
      console.error("Error checking Google:", error);
      setGoogleStatus({ connected: false });
    }
    setCheckingCalendars(false);
  };

  const navigateToCalendarSync = () => {
    navigate("/outlook-calendar");
  };

  const addUnavailableDate = async () => {
    if (!startDate || !endDate) {
      toast.error("Please select start and end dates");
      return;
    }

    try {
      await axios.post(
        `${API}/my-availability?start_date=${startDate}&end_date=${endDate}${reason ? `&reason=${encodeURIComponent(reason)}` : ""}`,
        {},
        { withCredentials: true }
      );
      
      toast.success("Unavailable dates added");
      setShowAddDialog(false);
      setStartDate("");
      setEndDate("");
      setReason("");
      fetchMyAvailability();
      fetchCommitteeAvailability();
    } catch (error) {
      console.error("Error adding unavailable date:", error);
      toast.error("Failed to add unavailable dates");
    }
  };

  const deleteAvailability = async (availabilityId) => {
    if (!window.confirm("Remove this unavailable period?")) {
      return;
    }

    try {
      await axios.delete(`${API}/my-availability/${availabilityId}`, {
        withCredentials: true
      });
      
      toast.success("Unavailable period removed");
      fetchMyAvailability();
      fetchCommitteeAvailability();
    } catch (error) {
      console.error("Error deleting availability:", error);
      toast.error("Failed to remove unavailable period");
    }
  };

  const findBestDates = async () => {
    if (!searchStartDate || !searchEndDate) {
      toast.error("Please select search date range");
      return;
    }

    try {
      const response = await axios.get(
        `${API}/committee/best-dates?start_date=${searchStartDate}&end_date=${searchEndDate}`,
        { withCredentials: true }
      );
      
      setBestDates(response.data.best_dates || []);
      setShowBestDatesDialog(true);
    } catch (error) {
      console.error("Error finding best dates:", error);
      toast.error("Failed to find best dates");
    }
  };

  if (!isCommitteeMember) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center p-4">
        <Card className="p-8 max-w-md text-center bg-slate-900/50 border border-white/5 rounded-xl">
          <Users className="w-16 h-16 text-slate-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Committee Access Required</h2>
          <p className="text-slate-400 mb-4">
            This page is only accessible to committee members.
          </p>
          <Button onClick={() => navigate("/dashboard")} className="bg-yellow-500/100 hover:bg-yellow-400 text-black">
            Back to Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0f1a]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-yellow-500 mx-auto"></div>
          <p className="mt-4 text-slate-300 font-medium">Loading availability...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0f1a] p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <Button
            onClick={() => navigate("/dashboard")}
            variant="ghost"
            className="text-slate-400 hover:text-white hover:bg-white/5"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Dashboard
          </Button>

          <div className="flex items-center gap-2">
            {canFindBestDates && (
              <Button
                onClick={() => setShowBestDatesDialog(true)}
                variant="outline"
                size="sm"
                className="border-slate-600 text-slate-300 hover:bg-slate-800"
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                Find Best Dates
              </Button>
            )}
            <Button
              onClick={() => setShowAddDialog(true)}
              size="sm"
              className="bg-yellow-500/100 hover:bg-yellow-400 text-black"
            >
              <Plus className="w-4 h-4 mr-2" />
              Mark Unavailable
            </Button>
          </div>
        </div>

        <h1 className="text-3xl font-bold text-white mb-2 font-display">Committee Availability</h1>
        <p className="text-slate-400 mb-4">
          View and manage committee member availability for meetings and events
        </p>

        {/* Calendar Connections Card */}
        <Card className="p-6 bg-slate-900/50 border border-blue-500/30 rounded-xl mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center justify-center w-12 h-12 bg-blue-500/20 rounded-full">
                <CalendarIcon className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">📅 Calendar Connections</h3>
                <p className="text-sm text-slate-400 mt-1">
                  Connect your calendars to share your availability
                </p>
              </div>
            </div>
            <Button
              onClick={navigateToCalendarSync}
              className="bg-yellow-500 hover:bg-yellow-400 text-black"
            >
              Manage Calendars
            </Button>
          </div>
          
          {/* Connection Status */}
          <div className="flex flex-wrap gap-3">
            {/* Outlook Status */}
            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
              outlookStatus?.connected ? 'bg-green-500/20 border border-green-500/30' : 'bg-slate-800/50 border border-slate-700'
            }`}>
              <Mail className={`w-4 h-4 ${outlookStatus?.connected ? 'text-green-400' : 'text-slate-400'}`} />
              <span className={`text-sm ${outlookStatus?.connected ? 'text-green-300' : 'text-slate-400'}`}>
                {outlookStatus?.connected ? (
                  <>
                    <CheckCircle className="w-3 h-3 inline mr-1" />
                    Outlook: {outlookStatus.outlook_email}
                  </>
                ) : (
                  'Outlook: Not connected'
                )}
              </span>
            </div>
            
            {/* Google Status */}
            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
              googleStatus?.connected ? 'bg-green-500/20 border border-green-500/30' : 'bg-slate-800/50 border border-slate-700'
            }`}>
              <svg className="w-4 h-4" viewBox="0 0 24 24">
                <path fill={googleStatus?.connected ? "#34A853" : "#666"} d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
              </svg>
              <span className={`text-sm ${googleStatus?.connected ? 'text-green-300' : 'text-slate-400'}`}>
                {googleStatus?.connected ? (
                  <>
                    <CheckCircle className="w-3 h-3 inline mr-1" />
                    Google: {googleStatus.google_email}
                  </>
                ) : (
                  'Google: Not connected'
                )}
              </span>
            </div>
          </div>
        </Card>

        {/* Time-Based Unavailability Card */}
        <Card className="p-6 bg-slate-900/50 border border-purple-500/30 rounded-xl mb-6">
          <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-purple-400" />
            Mark Unavailable Time
          </h2>
          <p className="text-sm text-slate-400 mb-4">
            Add specific times when you're not available (e.g., "Feb 2, 1pm-6pm" for a meeting)
          </p>
          
          {/* Add Form */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <div>
              <label className="text-xs text-slate-400 block mb-1">Date</label>
              <Input
                type="date"
                value={unavailDate}
                onChange={(e) => setUnavailDate(e.target.value)}
                className="bg-slate-800 border-slate-700"
              />
            </div>
            
            {!isAllDay && (
              <>
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Start Time</label>
                  <Input
                    type="time"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-400 block mb-1">End Time</label>
                  <Input
                    type="time"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
              </>
            )}
            
            <div>
              <label className="text-xs text-slate-400 block mb-1">Reason (optional)</label>
              <Input
                type="text"
                placeholder="e.g., Board meeting"
                value={unavailReason}
                onChange={(e) => setUnavailReason(e.target.value)}
                className="bg-slate-800 border-slate-700"
              />
            </div>
          </div>
          
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 cursor-pointer">
              <Switch
                checked={isAllDay}
                onCheckedChange={setIsAllDay}
              />
              <span className="text-sm text-slate-300">All day</span>
            </label>
            
            <Button
              onClick={addTimeUnavailability}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add
            </Button>
          </div>
          
          {/* List of time-based unavailability */}
          {timeUnavailability.length > 0 && (
            <div className="mt-4 pt-4 border-t border-slate-700">
              <h3 className="text-sm font-bold text-white mb-2">Your Scheduled Unavailable Times</h3>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {timeUnavailability.map((unavail) => {
                  const startDt = new Date(unavail.start_datetime);
                  const endDt = new Date(unavail.end_datetime);
                  const isAllDayEntry = unavail.is_all_day || 
                    (startDt.getHours() === 0 && startDt.getMinutes() === 0 && 
                     endDt.getHours() === 23 && endDt.getMinutes() === 59);
                  
                  return (
                    <div
                      key={unavail.id}
                      className="flex items-center justify-between p-2 bg-slate-800/50 rounded"
                    >
                      <div>
                        <p className="text-sm text-white">
                          {startDt.toLocaleDateString('en-SG', { weekday: 'short', month: 'short', day: 'numeric' })}
                          {isAllDayEntry ? (
                            <span className="text-purple-400 ml-2">(All day)</span>
                          ) : (
                            <span className="text-slate-400 ml-2">
                              {startDt.toLocaleTimeString('en-SG', { hour: '2-digit', minute: '2-digit' })} - {endDt.toLocaleTimeString('en-SG', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          )}
                        </p>
                        {unavail.reason && (
                          <p className="text-xs text-slate-400">{unavail.reason}</p>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteTimeUnavailability(unavail.id)}
                        className="text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* My Availability */}
          <Card className="p-6 bg-slate-900/50 border border-white/5 rounded-xl">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <CalendarIcon className="w-5 h-5 text-yellow-500" />
              My Unavailable Dates
            </h2>

            {myAvailability.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <p>You haven't marked any unavailable dates</p>
                <p className="text-sm mt-2">Click "Mark Unavailable" to add dates</p>
              </div>
            ) : (
              <div className="space-y-3">
                {myAvailability.map((avail) => (
                  <div
                    key={avail.id}
                    className="p-4 bg-slate-800/50 rounded-lg border border-white/5 flex items-start justify-between"
                  >
                    <div className="flex-1">
                      <p className="font-medium text-white">
                        {new Date(avail.start_date).toLocaleDateString()} - {new Date(avail.end_date).toLocaleDateString()}
                      </p>
                      {avail.reason && (
                        <p className="text-sm text-slate-400 mt-1">{avail.reason}</p>
                      )}
                    </div>
                    <Button
                      onClick={() => deleteAvailability(avail.id)}
                      variant="ghost"
                      size="sm"
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Committee Availability */}
          <Card className="p-6 bg-slate-900/50 border border-white/5 rounded-xl">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-yellow-500" />
              Committee Members
            </h2>

            {committeeAvailability.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <p>No committee members found</p>
              </div>
            ) : (
              <div className="space-y-4">
                {committeeAvailability.map((member) => (
                  <div key={member.user_id} className="p-4 bg-slate-800/50 rounded-lg border border-white/5">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="font-medium text-white">{member.name}</p>
                        <p className="text-xs text-slate-400 capitalize">{member.role.replace('_', ' ')}</p>
                      </div>
                      <div className="text-right">
                        {member.unavailable_dates.length === 0 ? (
                          <span className="text-xs text-emerald-400 font-medium">✓ Available</span>
                        ) : (
                          <span className="text-xs text-orange-400 font-medium">
                            {member.unavailable_dates.length} period{member.unavailable_dates.length !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                    </div>

                    {member.unavailable_dates.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {member.unavailable_dates.map((avail, idx) => (
                          <div key={idx} className="text-xs text-slate-300 bg-slate-900/50 p-2 rounded">
                            <span className="font-medium">
                              {new Date(avail.start_date).toLocaleDateString()} - {new Date(avail.end_date).toLocaleDateString()}
                            </span>
                            {avail.reason && <span className="text-slate-400"> • {avail.reason}</span>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* Add Unavailable Dialog */}
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Mark Unavailable Dates</DialogTitle>
            </DialogHeader>

            <div className="space-y-4 mt-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Start Date</label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">End Date</label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Reason (Optional)</label>
                <Input
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g., Annual leave, Business trip"
                />
              </div>

              <div className="flex gap-2 justify-end">
                <Button
                  onClick={() => setShowAddDialog(false)}
                  variant="ghost"
                >
                  Cancel
                </Button>
                <Button
                  onClick={addUnavailableDate}
                  className="bg-yellow-500/100 hover:bg-yellow-400 text-black"
                >
                  Add Unavailable Dates
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Best Dates Dialog */}
        <Dialog open={showBestDatesDialog} onOpenChange={setShowBestDatesDialog}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Find Best Meeting Dates</DialogTitle>
            </DialogHeader>

            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Search From</label>
                  <Input
                    type="date"
                    value={searchStartDate}
                    onChange={(e) => setSearchStartDate(e.target.value)}
                  />
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">Search To</label>
                  <Input
                    type="date"
                    value={searchEndDate}
                    onChange={(e) => setSearchEndDate(e.target.value)}
                  />
                </div>
              </div>

              <Button
                onClick={findBestDates}
                className="w-full bg-yellow-500/100 hover:bg-yellow-600 text-black"
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                Find Best Dates
              </Button>

              {bestDates.length > 0 && (
                <div className="mt-6">
                  <h3 className="font-bold text-gray-900 mb-3">Top 10 Best Dates</h3>
                  <div className="space-y-2">
                    {bestDates.map((date, idx) => (
                      <div
                        key={idx}
                        className={`p-3 rounded-lg border-2 ${
                          date.availability_percentage === 100
                            ? 'bg-green-500/10 border-green-500/30'
                            : date.availability_percentage >= 75
                            ? 'bg-yellow-500/10 border-yellow-500/30'
                            : 'bg-orange-500/10 border-orange-500/30'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-gray-900">
                              {new Date(date.date).toLocaleDateString('en-US', { 
                                weekday: 'short', 
                                year: 'numeric', 
                                month: 'short', 
                                day: 'numeric' 
                              })}
                              {date.is_weekend && <span className="ml-2 text-xs text-blue-600">Weekend</span>}
                            </p>
                            {date.unavailable_members.length > 0 && (
                              <p className="text-xs text-gray-600 mt-1">
                                Unavailable: {date.unavailable_members.join(', ')}
                              </p>
                            )}
                          </div>
                          <div className="text-right">
                            <p className={`font-bold text-lg ${
                              date.availability_percentage === 100
                                ? 'text-green-600'
                                : date.availability_percentage >= 75
                                ? 'text-yellow-600'
                                : 'text-orange-600'
                            }`}>
                              {date.availability_percentage}%
                            </p>
                            <p className="text-xs text-gray-600">
                              {date.available_count}/{date.total_members} available
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
